import { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateSiteThemeType } from "../actions";

const STORAGE_KEY = "localSiteTheme";

const normalizeTheme = (value) => {
    if (value === "black" || value === "dark" || value === "dark-AO") return "black";
    return "white";
};

const getDomTheme = (theme) => {
    return normalizeTheme(theme) === "black" ? "dark-AO" : "light-AO";
};

const readLocalTheme = () => {
    try {
        return normalizeTheme(window.localStorage.getItem(STORAGE_KEY) || "white");
    } catch (_e) {
        return "white";
    }
};

const saveLocalTheme = (theme) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, normalizeTheme(theme));
    } catch (_e) { }
};

const applyThemeToDocument = (theme) => {
    if (typeof document === "undefined") return;

    const normalized = normalizeTheme(theme);
    const domTheme = getDomTheme(normalized);
    const root = document.documentElement;
    const body = document.body;

    root.setAttribute("data-theme", domTheme);
    root.classList.remove("dark-AO", "light-AO");
    root.classList.add(domTheme);

    if (body) {
        body.classList.remove("dark-AO", "light-AO");
        body.classList.add(domTheme);
    }
};

const useSiteTheme = () => {
    const dispatch = useDispatch();
    const { user, authenticate } = useSelector((state) => state.auth || {});

    const userTheme = normalizeTheme(user?.siteThemeType);
    const [localSiteTheme, setLocalSiteTheme] = useState(readLocalTheme);
    const [optimisticTheme, setOptimisticTheme] = useState(null);
    const [pending, setPending] = useState(false);

    const siteTheme = useMemo(() => {
        if (optimisticTheme) return normalizeTheme(optimisticTheme);
        if (authenticate) return userTheme;

        return normalizeTheme(localSiteTheme);
    }, [authenticate, userTheme, localSiteTheme, optimisticTheme]);

    const domTheme = getDomTheme(siteTheme);
    const themeClass = domTheme;
    const isDark = siteTheme === "black";

    useEffect(() => {
        applyThemeToDocument(siteTheme);
        saveLocalTheme(siteTheme);
    }, [siteTheme]);

    useEffect(() => {
        if (!authenticate) return;

        setLocalSiteTheme(userTheme);

        if (optimisticTheme && optimisticTheme === userTheme) {
            setOptimisticTheme(null);
            setPending(false);
        }
    }, [authenticate, userTheme, optimisticTheme]);

    const setSiteTheme = useCallback(async (nextTheme) => {
        if (pending) return siteTheme;

        const previousTheme = siteTheme;
        const normalized = normalizeTheme(nextTheme);

        setPending(true);
        setOptimisticTheme(normalized);
        setLocalSiteTheme(normalized);
        applyThemeToDocument(normalized);
        saveLocalTheme(normalized);

        if (!authenticate) {
            setPending(false);
            return normalized;
        }

        try {
            await dispatch(updateSiteThemeType({ siteThemeType: normalized }));
            setPending(false);
            return normalized;
        } catch (error) {
            setOptimisticTheme(null);
            setLocalSiteTheme(previousTheme);
            applyThemeToDocument(previousTheme);
            saveLocalTheme(previousTheme);
            setPending(false);
            throw error;
        }
    }, [authenticate, dispatch, pending, siteTheme]);

    const toggleSiteTheme = useCallback(() => {
        const nextTheme = siteTheme === "black" ? "white" : "black";

        return setSiteTheme(nextTheme);
    }, [setSiteTheme, siteTheme]);

    return [
        siteTheme,
        toggleSiteTheme,
        {
            setSiteTheme,
            themeClass,
            domTheme,
            isDark,
            isLight: !isDark,
            pending,
        },
    ];
};

export default useSiteTheme;